import { Avatar, Flex, Tag, Tooltip } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { BodyOf, Messages } from "../../core/api";
import CaregiverStatusSelect from "../../shared/components/CaregiverStatusSelect";
import DataTable from "../../shared/components/DataTable/DataTable";
import useEndpointDataTable from "../../shared/components/DataTable/useEndpointDataTable";
import DebouncedInput from "../../shared/components/DebouncedInput";
import OfficesSelect from "../../shared/components/OfficeSelect";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import { OfficeId } from "../../shared/schema/schema";
import { toArrayOrNull } from "../../shared/utils/common";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { getEntityStatusColorScheme } from "../../shared/utils/get-entity-status-color-scheme";

/**
 * The compliance dashboard's grid, as a React table.
 *
 * The AngularJS component this replaces filtered the whole agency in the
 * browser. This is the same shape as the caregivers list: `useEndpointDataTable`
 * owns paging, sorting and filter state, and every change becomes one request
 * for one page.
 *
 * Mounted into the AngularJS page through the react-adapter, so the tabs, cards
 * and charts around it are untouched.
 */

type Row = Messages["ComplianceDashboardPageRow"];
type PageBody = BodyOf<"post", "./compliance/page">;
type ComplianceDocument = Messages["ComplianceDocument"];

type Filters = {
  search: string | null;
  statuses: Messages["CaregiverStatus"][] | null;
  certifications: string[] | null;
  complianceStatus: "compliant" | "not-compliant" | null;
  officeIds: OfficeId[] | null;
};

const CERTIFICATION_OPTIONS = ["HHA", "PCA", "RN"].map((c) => ({ value: c, label: c }));

const COMPLIANCE_STATUS_OPTIONS = [
  { value: "compliant", label: "Compliant" },
  { value: "not-compliant", label: "Not compliant" },
] as const;

const columnHelper = createColumnHelper<Row>();

type Item = Row["items"][number];
type Cell = { text: string; scheme: string };

const NEVER = LocalDate.parse("3000-01-01");

/**
 * What one document-type cell says, ported from the AngularJS grid's
 * parseTableItemDisplayData / getItemColorLabel. Kept identical on purpose: the
 * matrix is the thing people read this page for, and a cell that says something
 * different from the page it replaces is worse than one that says nothing.
 */
function itemCell(item: Item): Cell {
  const soon = LocalDate.now().plusDays(30);

  switch (item.status) {
    // No "Not required" case: that is not a status the server sends. It is what
    // the absence of an item means, and the cell handles that below.
    case "Resolved":
      return { text: "Resolved", scheme: "gray" };

    case "Not due yet": {
      const due = item.dueDate;
      return {
        text:
          due === null || due.compareTo(NEVER) === 0
            ? "Not due yet"
            : `Due in ${dateFormatter.toDate(due)}`,
        scheme: due !== null && due.compareTo(soon) > 0 ? "gray" : "orange",
      };
    }

    case "Pending Uploads":
      return { text: "Pending Uploads", scheme: "orange" };

    case "Missing":
      return { text: "Missing", scheme: "red" };

    case "Compliant":
    case "Not Compliant": {
      // The dates, not the status - the old grid showed "effective - expiry"
      // here, and green turns amber inside 30 days of expiry.
      const text = [item.effectiveDate, item.expiryDate]
        .flatMap((date) => (date === null ? [] : [dateFormatter.toDate(date)]))
        .join(" - ");

      if (item.status === "Not Compliant") {
        return { text: text, scheme: "red" };
      }

      const expiring = item.expiryDate !== null && item.expiryDate.compareTo(soon) < 0;
      return { text: text, scheme: expiring ? "orange" : "green" };
    }

    default:
      return { text: String(item.status), scheme: "gray" };
  }
}

export default function ComplianceDashboardTable() {
  const { api } = useApi();

  // The document types are columns, and they arrive with the page rather than
  // from an endpoint of their own. Held here so the column set survives the
  // next request instead of collapsing while it is in flight.
  const [documents, setDocuments] = React.useState<ComplianceDocument[]>([]);

  /**
   * followupDocumentTypeId -> the document type that asks for it.
   *
   * Not a field on the wire: the AngularJS parser derives it by walking every
   * document's Dropdown fields for options that require a follow-up. A document
   * in this map is only required once its parent has asked for it.
   */
  const followupParents = React.useMemo(() => {
    const parents = new Map<number, number>();

    for (const document of documents) {
      for (const field of document.fields) {
        if (field.type !== "Dropdown") {
          continue;
        }

        for (const option of field.possibleValues) {
          const followupId = option.followupDocumentRequireData?.followupDocumentTypeId;

          if (followupId !== undefined && followupId !== null) {
            parents.set(followupId as unknown as number, document.id as unknown as number);
          }
        }
      }
    }

    return parents;
  }, [documents]);

  const columns = React.useMemo(() => {
    const base = [
      columnHelper.accessor((row) => row.caregiver.displayName, {
        id: "name",
        header: "Caregiver",
        meta: { gqlSortKey: "name" },
        cell: (info) => {
          const caregiver = info.row.original.caregiver;
          return (
            <Flex align="center" gap={2}>
              <Avatar
                name={caregiver.displayName}
                size="xs"
                src={caregiver.photoUrl ?? undefined}
              />
              {caregiver.displayName}
            </Flex>
          );
        },
      }),
      columnHelper.accessor((row) => row.caregiver.displayId, {
        id: "displayId",
        header: "ID",
        meta: { gqlSortKey: "displayId" },
      }),
      columnHelper.accessor((row) => row.caregiver.status, {
        id: "status",
        header: "Status",
        meta: { gqlSortKey: "status" },
        cell: (info) => {
          const caregiver = info.row.original.caregiver;
          return (
            <Flex align="center" gap={2}>
              <Tag colorScheme={getEntityStatusColorScheme(caregiver.status)}>
                {caregiver.status}
              </Tag>
              {caregiver.nextStatus !== null && (
                <Tooltip label={`From ${caregiver.nextStatus.effectiveDate.toString()}`}>
                  <Tag colorScheme="gray">{caregiver.nextStatus.status}</Tag>
                </Tooltip>
              )}
            </Flex>
          );
        },
      }),
      columnHelper.accessor((row) => row.caregiver.isCompliant, {
        id: "isCompliant",
        header: "Compliance",
        cell: (info) => (
          <Tag colorScheme={info.getValue() ? "green" : "red"}>
            {info.getValue() ? "Compliant" : "Not compliant"}
          </Tag>
        ),
      }),
      columnHelper.accessor((row) => row.caregiver.certifications.join(", "), {
        id: "certifications",
        header: "Certifications",
      }),
      columnHelper.accessor((row) => row.caregiver.officeNames.join(", "), {
        id: "offices",
        header: "Offices",
      }),
    ];

    // The matrix: one column per document type, grouped under its section the
    // way the AngularJS grid grouped them, so the header still reads
    // "I9 | Medical | General" across the top.
    const bySection = new Map<string, typeof documents>();

    for (const document of documents) {
      const section = document.section ?? "Other";
      bySection.set(section, [...(bySection.get(section) ?? []), document]);
    }

    const documentColumns = [...bySection].map(([section, sectionDocuments]) =>
      columnHelper.group({
        id: `section-${section}`,
        header: section,
        columns: sectionDocuments.map((document) =>
          columnHelper.display({
            id: `document-${document.id}`,
            header: document.name,
            cell: (info) => {
              const row = info.row.original;
              const item = row.items.find((candidate) => candidate.documentTypeId === document.id);

              if (item !== undefined) {
                const cell = itemCell(item);
                return (
                  <Tag colorScheme={cell.scheme} size="sm" whiteSpace="nowrap">
                    {cell.text}
                  </Tag>
                );
              }

              // No item recorded. A follow-up document is only required once
              // its parent asked for it - the server says which through
              // missingFollowupDocumentTypeIds - so anything else is Missing
              // rather than blank. Blank was the thing that made this table
              // look empty.
              const isRequired =
                !followupParents.has(document.id as unknown as number) ||
                row.missingFollowupDocumentTypeIds.includes(document.id);

              return isRequired ? (
                <Tag colorScheme="red" size="sm">
                  Missing
                </Tag>
              ) : (
                <Tag colorScheme="gray" size="sm">
                  Not required
                </Tag>
              );
            },
          })
        ),
      })
    );

    return [...base, ...documentColumns];
  }, [documents, followupParents]);

  const { table, query, filters, setFilter, metaButtonProps } = useEndpointDataTable({
    queryKey: ["compliance", "dashboard", "page"],
    columns: columns,
    columnVisiblity: {
      storage: { key: "compliance/dashboard", version: 2 },
      forceVisibleColumns: React.useMemo(() => ["name"], []),
      // The document matrix is what this page is for, so it is on by default.
      // Built from the response, so it only fills in once a page has arrived -
      // until then the caregiver columns stand alone.
      initialSelected: React.useMemo(
        () =>
          [
            "@name",
            "@displayId",
            "@status",
            "@isCompliant",
            "@certifications",
            ...documents.map((document) => `@document-${document.id}` as const),
          ] as `@${string}`[],
        [documents]
      ),
    },
    initialSorting: [{ id: "name", desc: false }],
    filters: {
      initialState: {
        search: null,
        // The population the AngularJS dashboard opened on, so the count does
        // not move under anyone the day this replaces it.
        statuses: ["ACTIVE"],
        certifications: ["HHA", "PCA"],
        complianceStatus: null,
        officeIds: null,
      } satisfies Filters as Filters,
    },
    fetchPage: async (request) => {
      const body: PageBody = {
        search: request.filters.search,
        // The endpoint takes arrays rather than nullables here, and maps an
        // empty one to "not filtering" - see the `length > 0 ? ... : null` in
        // CaregiverSelection. So dropping a filter sends [], not null.
        statuses: request.filters.statuses ?? [],
        certifications: request.filters.certifications ?? [],
        isCompliant:
          request.filters.complianceStatus === null
            ? null
            : request.filters.complianceStatus === "compliant",
        isFromLandingPage: null,
        hasHireDate: null,
        officeIds: request.filters.officeIds ?? [],
        caregiverIds: null,
        sortField: request.sortField as PageBody["sortField"],
        sortOrder: request.sortOrder,
        limit: request.limit,
        offset: request.offset,
      };

      const response = await api.post("./compliance/page", { body });

      setDocuments(response.documents);

      return { items: response.rows, totalCount: response.totalCount };
    },
    // Serving one page at a time is the point of this endpoint; an export that
    // walked every page would rebuild the load this replaced. Left empty until
    // someone asks for it, rather than quietly re-introducing it.
    fetchAllForExport: async () => [],
  });

  const filterNode = (
    <Flex gap={3} wrap="wrap">
      <DebouncedInput
        debounce={200}
        placeholder="Search name or ID"
        value={filters.search ?? ""}
        onChange={(x) => setFilter("search", x === "" ? null : String(x))}
      />

      <CaregiverStatusSelect
        multiple={true}
        value={toArrayOrNull(filters.statuses)}
        onChange={(x) => setFilter("statuses", x ?? null)}
      />

      <Select
        label="Certifications"
        multiple={true}
        options={CERTIFICATION_OPTIONS}
        value={toArrayOrNull(filters.certifications)}
        onChange={(x) => setFilter("certifications", x ?? null)}
      />

      <Select
        label="Compliance"
        multiple={false}
        options={COMPLIANCE_STATUS_OPTIONS}
        value={filters.complianceStatus}
        onChange={(x) => setFilter("complianceStatus", x ?? null)}
      />

      <OfficesSelect
        multiple={true}
        value={filters.officeIds ?? null}
        onChange={(x) => setFilter("officeIds", x ?? null)}
      />
    </Flex>
  );

  return (
    <DataTable
      filterNode={filterNode}
      filterNodeFlexProps={{ alignItems: "flex-start", direction: "column" }}
      isLoading={query.isPending}
      metaButtonProps={metaButtonProps}
      spacing="tight"
      table={table}
    />
  );
}
